import type { ShallowRef } from 'vue';
import { getExtParams, setExtParams } from 'src/services/extParams';
import { isGeoItemMetaComment } from 'src/lib/geoItemHelper';
import { MOVIE_SEARCH_RESULT_MODES } from 'src/consts/top';
import type { GeoItemsMeta, GIMovieList } from 'src/models/geoItem';
import type { Location, MapElemInfo, MovieLayerMeta, QueryArea } from 'src/models';
import type { GIResource, GIResourceWithID, MapSelectOpts } from 'src/models/geoItem';

type ExtParamsContext = {
  handleExtParams: () => void;
};

// インフラドクター連携・付箋通知・動画共有リンクなど、URLパラメータ(?lat=&lon=等)を
// 起点にした外部連携の初期表示処理。onMounted完了後（データレイヤー初期化後）に一度呼ぶ。
export function useExtParams(
  geoItemsMeta: GeoItemsMeta,
  movieLayerMeta: ShallowRef<MovieLayerMeta>,
  deps: {
    getAreaQueryRadiusMeter: () => number;
    doAreaMovieSearch: (queryArea: QueryArea) => void;
    setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null, opts?: MapSelectOpts) => void;
    getNewObjForMapSelectedElem: (queryArea: QueryArea) => MapElemInfo<Location>;
    onTabElementClick: (element: MapElemInfo<GIResourceWithID>) => Promise<void>;
    setMovieQueryStart: (params: { mode: 'id' }) => void;
    setMovieQueryDone: (params: { results: GIMovieList[] }) => void;
    onMovieSearchResultRowClick: (obj: GIMovieList) => void;
  },
): ExtParamsContext {
  const fetchAndShowCommentById = async(commentId: string) => {
    const dataName = 'comment';
    const metaItem = geoItemsMeta.map[dataName];
    if (!isGeoItemMetaComment(metaItem)) return;

    const comment = await metaItem.manager.getResourceById(parseInt(commentId));
    if (!comment) return;

    await deps.onTabElementClick({ dataName, data: comment });
  };
  const fetchAndShowMovieByIdAndOffset = async(movieId: number, startOffset: number) => {
    const metaItem = movieLayerMeta.value;
    if (!metaItem.manager) return;

    deps.setMovieQueryStart({ mode: MOVIE_SEARCH_RESULT_MODES.ID });
    const movieList = await metaItem.manager.getResourceById(movieId);
    const results = movieList ? [movieList] : [];
    deps.setMovieQueryDone({ results });
    if (!movieList) return;

    movieList.startOffset = startOffset;
    deps.onMovieSearchResultRowClick(movieList);
  };
  const handleExtParams = () => {
    // インフラドクター連携 (URLパラメータにlat,lon指定でこちらの画面が開かれる)
    // lat, lonがあったらそれで検索をかける
    const extParams = getExtParams();
    // 一度読んだら捨てる
    setExtParams({});
    if (extParams?.lat && extParams?.lon) {
      const queryArea: QueryArea = {
        lat: extParams.lat,
        lon: extParams.lon,
        radius: deps.getAreaQueryRadiusMeter(),
      };
      deps.doAreaMovieSearch(queryArea);
      deps.setMapSelectedElemInfo(
        deps.getNewObjForMapSelectedElem(queryArea));
    }
    if (extParams.commentId) {
      fetchAndShowCommentById(extParams.commentId);
    } else if (extParams.movieId && extParams.startOffset != null) {
      fetchAndShowMovieByIdAndOffset(extParams.movieId, extParams.startOffset);
    }
  };

  return { handleExtParams };
}
